import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildCategoryOptions,
  buildSalesCodeOptions,
} from '../../ProductsServices/useProductFormData';

const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
const sameName = (a, b) =>
  String(a || '')
    .trim()
    .toLowerCase() ===
  String(b || '')
    .trim()
    .toLowerCase();

// What the import needs to know about the studio: the products it already has,
// its vendors, categories and sales codes.
export default function useStudioCatalog(api, itemType) {
  const [context, setContext] = useState(null);
  const [loadError, setLoadError] = useState('');

  const reload = useCallback(async () => {
    setLoadError('');
    try {
      const data = await api.loadContext();
      setContext(data);
      return data;
    } catch (err) {
      console.error('Failed to load the studio catalog', err);
      setLoadError(err?.message || "Couldn't load this studio's catalog.");
      return null;
    }
  }, [api]);

  useEffect(() => {
    reload();
  }, [reload]);

  const vendors = useMemo(() => context?.vendors || [], [context]);
  const categories = useMemo(() => context?.categories || [], [context]);
  const categoryOptions = useMemo(
    () => buildCategoryOptions(categories, itemType),
    [categories, itemType]
  );
  const salesCodeOptions = useMemo(() => buildSalesCodeOptions(context?.salesAccounts), [context]);

  // Resolves to { vendorId, name, __duplicate } like the Add Product form's
  // addVendor, or null when the vendor couldn't be saved.
  const addVendor = async (name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return null;
    const known = vendors.find((v) => sameName(v.name, trimmed));
    if (known) return { ...known, __duplicate: true };
    try {
      const saved = await api.ensureVendor(trimmed);
      const vendor = { vendorId: saved.vendorId, name: saved.name || trimmed };
      setContext((prev) =>
        prev && !prev.vendors.some((v) => v.vendorId === vendor.vendorId)
          ? { ...prev, vendors: [...prev.vendors, vendor].sort(byName) }
          : prev
      );
      return { ...vendor, __duplicate: Boolean(saved.duplicate) };
    } catch (err) {
      console.error('Failed to add vendor', err);
      return null;
    }
  };

  const addCategory = async (name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed || categories.some((c) => sameName(c.name, trimmed))) return;
    try {
      const saved = await api.ensureCategory(trimmed, itemType);
      const category = { name: saved.name || trimmed, type: itemType };
      setContext((prev) =>
        prev && !prev.categories.some((c) => sameName(c.name, category.name))
          ? { ...prev, categories: [...prev.categories, category].sort(byName) }
          : prev
      );
    } catch (err) {
      console.error('Failed to add category', err);
    }
  };

  return {
    loaded: Boolean(context),
    loadError,
    reload,
    products: context?.products || [],
    vendors,
    categoryOptions,
    salesCodeOptions,
    addVendor,
    addCategory,
  };
}
